import * as React from 'react';
import { useThrottleCallback } from '@react-hook/throttle';
import { styled, useTheme } from '../theme-provider';
import { useTableContext } from './table';
import { ButtonNext } from '../button-next';
import { ButtonPrevious } from '../button-previous';
import { Text, TextTag, TokenTextAppearance } from '../text';
import { getValueForCurrentScreenSize, } from '../helpers/breakpoint';
const StyledTableNavigation = styled.div.withConfig({ displayName: "StyledTableNavigation", componentId: "sc-1cqorta" }) `width:${({ tableWidth }) => tableWidth}px;display:flex;justify-content:space-between;padding:${({ theme }) => theme.size.static200} 0;`;
const ColumnInfoText = ({ firstFullyVisibleColumn, lastFullyVisibleColumn }) => {
    const { columnCount = 1, fixedHeaderColumn } = useTableContext();
    const from = (firstFullyVisibleColumn !== null && firstFullyVisibleColumn !== void 0 ? firstFullyVisibleColumn : 0) + 1;
    const to = Math.max((lastFullyVisibleColumn !== null && lastFullyVisibleColumn !== void 0 ? lastFullyVisibleColumn : 0) + (fixedHeaderColumn ? 0 : 1), 1);
    const text = Array.from(new Set([from, to]))
        .sort((a, b) => a - b)
        .join('-');
    return (React.createElement(Text, { appearance: TokenTextAppearance.copy200, tag: TextTag.p },
        text,
        "/",
        React.createElement(Text, { appearance: TokenTextAppearance.copy200, tag: TextTag.span, bold: true }, fixedHeaderColumn ? columnCount - 1 : columnCount)));
};
export const TableNavigation = ({ columns, hideNavigationColumnNumbers, onClickPrevious, onClickNext }) => {
    const { columnCount = 1, tableWrapperRef, tableWidth } = useTableContext();
    const { direction, size: { static350: gutterWidth }, } = useTheme();
    const [firstFullyVisibleColumn, setFirstFullyVisibleColumn] = React.useState(0);
    const [lastFullyVisibleColumn, setLastFullyVisibleColumn] = React.useState(columnCount - 1);
    const [oneColumnWidthWithGutter, setOneColumnWidthWithGutter] = React.useState(0);
    const scrollForward = React.useCallback(() => {
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const nextIndex = Math.min(lastFullyVisibleColumn + 1, columnCount - 1);
        if (onClickNext) {
            onClickNext();
        }
        wrapper.scrollLeft =
            (direction === 'rtl' ? -1 : 1) *
                ((nextIndex + 1) * oneColumnWidthWithGutter -
                    parseInt(gutterWidth, 10) -
                    tableWidth);
    }, [
        tableWrapperRef,
        lastFullyVisibleColumn,
        columnCount,
        onClickNext,
        direction,
        oneColumnWidthWithGutter,
        gutterWidth,
        tableWidth,
    ]);
    const scrollBackward = React.useCallback(() => {
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const previousIndex = Math.max(firstFullyVisibleColumn - 1, 0);
        if (onClickPrevious) {
            onClickPrevious();
        }
        wrapper.scrollLeft =
            (direction === 'rtl' ? -1 : 1) *
                (previousIndex * oneColumnWidthWithGutter);
    }, [
        tableWrapperRef,
        firstFullyVisibleColumn,
        onClickPrevious,
        direction,
        oneColumnWidthWithGutter,
    ]);
    const detectNumberOfColumnsInViewport = useThrottleCallback(React.useCallback(() => {
        var _a;
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        const currentNumberOfColumns = (_a = getValueForCurrentScreenSize(columns)) !== null && _a !== void 0 ? _a : columnCount;
        const oneColumnWidthWithGutter = (tableWidth + parseInt(gutterWidth, 10)) / currentNumberOfColumns;
        setOneColumnWidthWithGutter(oneColumnWidthWithGutter);
        if (currentNumberOfColumns < columnCount) {
            // number of scrolled columns, rounded to 1/2th of a column
            const scrolledColumns = Math.abs(Math.round((wrapper.scrollLeft / oneColumnWidthWithGutter) * 2) / 2);
            const firstFullyVisibleColumn = Math.ceil(scrolledColumns);
            const lastFullyVisibleColumn = Math.floor(scrolledColumns + currentNumberOfColumns) - 1;
            setFirstFullyVisibleColumn(firstFullyVisibleColumn);
            setLastFullyVisibleColumn(lastFullyVisibleColumn);
        }
        else {
            setFirstFullyVisibleColumn(0);
            setLastFullyVisibleColumn(columnCount - 1);
        }
    }, [columnCount, columns, tableWidth, tableWrapperRef, gutterWidth]));
    React.useEffect(() => {
        const wrapper = tableWrapperRef === null || tableWrapperRef === void 0 ? void 0 : tableWrapperRef.current;
        if (!wrapper) {
            return;
        }
        //detect the number of columns in viewport / the scroll distance on window load
        detectNumberOfColumnsInViewport();
        //update the number of columns in viewport on window resize
        window.addEventListener('resize', detectNumberOfColumnsInViewport);
        //update the number of columns in viewport on table scroll
        wrapper.addEventListener('scroll', detectNumberOfColumnsInViewport, {
            passive: true,
        });
        // detect table width when table becomes visible when it is initially hidden
        const observer = new ResizeObserver((entries, _observer) => {
            entries.forEach((entry) => {
                if (entry.contentRect.width > 0) {
                    detectNumberOfColumnsInViewport();
                }
            });
        });
        observer.observe(wrapper);
        return () => {
            window.removeEventListener('resize', detectNumberOfColumnsInViewport);
            wrapper.removeEventListener('scroll', detectNumberOfColumnsInViewport);
            observer.unobserve(wrapper);
            observer.disconnect(); // this is probably not necessary, but just in case ...
        };
    }, [detectNumberOfColumnsInViewport, tableWrapperRef]);
    return (React.createElement(StyledTableNavigation, { "data-testid": "navigation", tableWidth: tableWidth },
        React.createElement(ButtonPrevious, { tag: "button", size: "small", disabled: (firstFullyVisibleColumn !== null && firstFullyVisibleColumn !== void 0 ? firstFullyVisibleColumn : 0) === 0, onClick: scrollBackward, ariaLabel: "scroll table backwards" }),
        !hideNavigationColumnNumbers && (React.createElement(ColumnInfoText, { firstFullyVisibleColumn: firstFullyVisibleColumn, lastFullyVisibleColumn: lastFullyVisibleColumn })),
        React.createElement(ButtonNext, { tag: "button", size: "small", disabled: (lastFullyVisibleColumn !== null && lastFullyVisibleColumn !== void 0 ? lastFullyVisibleColumn : 0) === columnCount - 1, onClick: scrollForward, ariaLabel: "scroll table forwards" })));
};
